// ═══════════════════════════════════════════════════════════════
// Intake — Dashboard
// Welcome banner, 5 stat cards, recent activity, active incident
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import { usePolling } from '../../hooks/usePolling';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, SimpleGrid, Paper, Group, Badge, Stack, Button,
  ThemeIcon, Center, Alert, Avatar,
} from '../../components/gab/GabComponents';
import {
  IconUserPlus, IconUsers, IconLogout, IconAlertTriangle,
  IconArrowRight, IconHeartbeat, IconCircleCheck, IconUrgent,
  IconMapPin, IconCalendar,
} from '@tabler/icons-react';
import { useIntakeStore } from '../../store/intakeStore';
import { useAuthStore } from '../../store/authStore';
import { formatDateTime, TRIAGE_COLORS } from './lib/helpers';
import { primary } from '../../lib/colors';

export default function IntakeDashboard() {
  const navigate = useNavigate();
  const { individuals, loading, loadIndividuals, loadDischarges } = useIntakeStore();
  const { activeIncident } = useAuthStore();

  useEffect(() => {
    if (activeIncident) {
      loadIndividuals(String(activeIncident.id));
      loadDischarges(String(activeIncident.id));
    }
  }, [activeIncident, loadIndividuals, loadDischarges]);
  usePolling(() => {
    if (activeIncident) {
      loadIndividuals(String(activeIncident.id));
      loadDischarges(String(activeIncident.id));
    }
  });

  const incidentIndividuals = useMemo(() => {
    if (!activeIncident) return [];
    return individuals.filter(i => String(i.incidentId) === String(activeIncident.id));
  }, [individuals, activeIncident]);

  const stats = useMemo(() => {
    let green = 0, yellow = 0, red = 0, discharged = 0;
    for (const ind of incidentIndividuals) {
      if (ind.status === 'discharged') discharged++;
      const t = (ind.triage || 'var(--color-success)').toLowerCase();
      if (t === 'var(--color-success)') green++;
      else if (t === '#C68700') yellow++;
      else if (t === 'var(--color-error)') red++;
    }
    return { total: incidentIndividuals.length, green, yellow, red, discharged };
  }, [incidentIndividuals]);

  const recent = useMemo(() => {
    return [...incidentIndividuals]
      .sort((a, b) => (b.datecreated ?? '').localeCompare(a.datecreated ?? ''))
      .slice(0, 8);
  }, [incidentIndividuals]);

  // ── No incident selected ──────────────────────────────────
  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="#939598">
            <IconAlertTriangle size={30} />
          </ThemeIcon>
          <Title order={4}>No Incident Selected</Title>
          <Text size="sm" c="dimmed">
            Use the incident selector in the top header bar to choose an active incident.
          </Text>
        </Stack>
      </Center>
    );
  }

  // ── Stat card config ──────────────────────────────────────
  const statCards = [
    {
      label: 'Total Scanned',
      value: stats.total,
      color: primary[700],
      icon: IconUsers,
      subtitle: 'All registrations',
    },
    {
      label: 'Green',
      value: stats.green,
      color: TRIAGE_COLORS.green,
      icon: IconCircleCheck,
      subtitle: 'Minor / Walking',
    },
    {
      label: 'Yellow',
      value: stats.yellow,
      color: TRIAGE_COLORS.yellow,
      icon: IconHeartbeat,
      subtitle: 'Delayed Care',
    },
    {
      label: 'Red',
      value: stats.red,
      color: TRIAGE_COLORS.red,
      icon: IconUrgent,
      subtitle: 'Immediate',
    },
    {
      label: 'Discharged',
      value: stats.discharged,
      color: '#6b7280',
      icon: IconLogout,
      subtitle: 'Released / Transferred',
    },
  ];

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={3} c="black">Intake</Title>
          <Text size="sm" c="dimmed">
            {activeIncident.name} — {incidentIndividuals.length} total registrations
          </Text>
        </div>
        <Button
          size="sm"
          leftSection={<IconUserPlus size={14} />}
          onClick={() => navigate('/intake/register')}
        >
          New Registration
        </Button>
      </Group>

      {/* ── 5 Stat Cards ──────────────────────────────────── */}
      <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} mb="md" spacing="sm">
        {statCards.map((card) => (
          <Paper key={card.label} p="sm" radius="sm" withBorder ta="center"
            style={{ borderTop: `3px solid ${card.color}` }}>
            <Text size="xs" c="dimmed">{card.label}</Text>
            <Text fw={700} fz={28} style={{ color: card.color }}>
              {loading ? '...' : card.value}
            </Text>
            <Text size="xs" c="dimmed">{card.subtitle}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* ── Two-Column Grid ───────────────────────────────── */}
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* Recent Activity */}
        <Paper p="md" radius="sm" withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={600}>Recent Activity</Text>
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('/intake/roster')}
            >
              View Roster
            </Button>
          </Group>

          {recent.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">
              No registrations yet.
            </Text>
          ) : (
            <Stack gap="xs">
              {recent.map((ind) => (
                <Paper
                  key={ind.id}
                  p="sm"
                  radius="sm"
                  withBorder
                  style={{
                    borderLeft: `3px solid ${TRIAGE_COLORS[ind.triage] || '#6b7280'}`,
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate(`/intake/roster?highlight=${ind.id}`)}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                      <Avatar
                        size={32}
                        radius="xl"
                        src={ind.photoUrl || null}
                        color={ind.triage === 'var(--color-error)' ? 'var(--color-error)' : ind.triage === '#C68700' ? '#C68700' : 'var(--color-success)'}
                      >
                        {ind.firstName?.[0]}{ind.lastName?.[0]}
                      </Avatar>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <Text size="sm" fw={500} lineClamp={1}>
                          {ind.lastName}, {ind.firstName}
                        </Text>
                        <Group gap="xs">
                          <Badge
                            size="xs"
                            color={ind.triage === 'var(--color-error)' ? 'var(--color-error)' : ind.triage === '#C68700' ? '#C68700' : 'var(--color-success)'}
                            variant="filled"
                          >
                            {(ind.triage || 'var(--color-success)').toUpperCase()}
                          </Badge>
                          <Badge size="xs" variant="outline" color="#939598">{ind.tagCode}</Badge>
                          {ind.status === 'discharged' && (
                            <Badge size="xs" color="#939598">Discharged</Badge>
                          )}
                        </Group>
                      </div>
                    </Group>
                    <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(ind.datecreated)}
                    </Text>
                  </Group>
                </Paper>
              ))}
            </Stack>
          )}
        </Paper>

        {/* Active Incident Card */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} mb="sm">Active Incident</Text>

          <Alert
            variant="light"
            color="var(--color-primary-700)"
            radius="sm"
            mb="md"
            title={activeIncident.name}
          >
            <Stack gap="xs" mt="xs">
              {activeIncident.description && (
                <Text size="sm">{activeIncident.description}</Text>
              )}
              <Group gap="lg">
                {activeIncident.location && (
                  <Group gap={4}>
                    <IconMapPin size={14} />
                    <Text size="xs">{activeIncident.location}</Text>
                  </Group>
                )}
                {activeIncident.startDate && (
                  <Group gap={4}>
                    <IconCalendar size={14} />
                    <Text size="xs">{formatDateTime(activeIncident.startDate)}</Text>
                  </Group>
                )}
              </Group>
              {activeIncident.commander && (
                <Text size="xs" c="dimmed">IC: {activeIncident.commander}</Text>
              )}
            </Stack>
          </Alert>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            <Paper p="sm" radius="sm" withBorder>
              <Text size="xs" c="dimmed">Active</Text>
              <Text fw={700} fz="lg" style={{ color: TRIAGE_COLORS.green }}>
                {stats.total - stats.discharged}
              </Text>
            </Paper>
            <Paper p="sm" radius="sm" withBorder>
              <Text size="xs" c="dimmed">Discharged</Text>
              <Text fw={700} fz="lg" style={{ color: '#6b7280' }}>
                {stats.discharged}
              </Text>
            </Paper>
            <Paper p="sm" radius="sm" withBorder>
              <Text size="xs" c="dimmed">Green</Text>
              <Text fw={700} fz="lg" style={{ color: TRIAGE_COLORS.green }}>
                {stats.green}
              </Text>
            </Paper>
            <Paper p="sm" radius="sm" withBorder>
              <Text size="xs" c="dimmed">Yellow</Text>
              <Text fw={700} fz="lg" style={{ color: TRIAGE_COLORS.yellow }}>
                {stats.yellow}
              </Text>
            </Paper>
            <Paper p="sm" radius="sm" withBorder>
              <Text size="xs" c="dimmed">Red</Text>
              <Text fw={700} fz="lg" style={{ color: TRIAGE_COLORS.red }}>
                {stats.red}
              </Text>
            </Paper>
            <Paper p="sm" radius="sm" withBorder>
              <Text size="xs" c="dimmed">Incident Type</Text>
              <Text fw={500} size="sm" tt="capitalize">
                {activeIncident.incidentType || 'General'}
              </Text>
            </Paper>
          </SimpleGrid>
        </Paper>
      </SimpleGrid>
    </div>
  );
}
